import type { ReactNode } from "react"

interface SectionRuleProps {
  /** Optional orange eyebrow above the title */
  eyebrow?: string
  /** The section H2 */
  title: string
  /** Optional right-aligned meta text (e.g. "5 scenarios", "updated Mar 2026") */
  right?: ReactNode
  /** Additional classes on the outer wrapper */
  className?: string
}

/**
 * SectionRule — editorial section header with a hard black border-bottom
 * rule, used inside the body of tool-page variants to separate sections
 * without relying on whitespace alone.
 *
 * Similar in spirit to `IndexSectionHeader` (used on /tools), but with an
 * eyebrow slot above the title and lighter padding — designed for mid-page
 * use inside variant pages rather than indexing pages.
 */
export function SectionRule({ eyebrow, title, right, className = "" }: SectionRuleProps) {
  return (
    <div
      className={`flex items-end justify-between gap-4 border-b border-gray-900 pb-3 mb-6 ${className}`}
    >
      <div>
        {eyebrow && (
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-2 text-[#ff9951]">
            {eyebrow}
          </div>
        )}
        <h2
          className="m-0 text-gray-900 font-black"
          style={{ fontSize: 22, letterSpacing: "-0.02em", lineHeight: 1.1 }}
        >
          {title}
        </h2>
      </div>
      {right && (
        <div className="text-[11px] uppercase tracking-[0.15em] text-gray-500 whitespace-nowrap">
          {right}
        </div>
      )}
    </div>
  )
}
